import React, { useEffect, useState } from 'react';
import { Plus, Search, Circle, X, ChevronDown, Edit, Trash2, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { SignedImage, SignedLink } from '../common/SignedFile';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from '../../hooks/useNavigate';
import { ConfirmModal } from '../common/ConfirmModal';
import { useAuth } from '../../contexts/AuthContext';
import { parseTrailers, formatTrailers } from '../../lib/trailers';

// Format date without timezone conversion (shows exact date from database)
const formatDateNoTZ = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  // Parse the date string as-is without timezone conversion
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${month}/${day}/${year}`;
};

interface TruckData {
  id: string;
  unit_number: string;
  year: number;
  make: string;
  model: string;
  vin: string;
  license_plate: string;
  registration_expiration: string;
  dot_expiration: string;
  driver_status: 'OWNER' | 'COMPANY' | 'RENT' | 'BUYING' | 'UNASSIGNED';
  samsara_enabled: 'Yes' | 'No' | 'Prijavljena';
  prepass: string;
  ifta_2024?: string;
  ifta_2025?: string;
  ifta_2026?: string;
  ny_2025?: boolean;
  truck_status: 'ACTIVE' | 'ALERT' | 'POTENCIJALNI RECOVERY' | 'RECOVERY - 100%' | 'COMING BACK' | 'JARDA - AVAILABLE' | 'JARDA - VACATION' | 'SHOP/NA YARDI - nasi kamioni' | 'SHOP OWNERI' | 'VACATION - ALI NISU NA JARDI PARKIRANI' | 'INACTIVE';
  trailers: string[] | null;
  dispatcher_id?: string;
  company_id?: string;
  companies?: {
    id: string;
    name: string;
    code: string;
  };
  driver?: {
    id: string;
    first_name: string;
    last_name: string;
    driver_type?: 'company_driver' | 'owner_operator' | 'rent' | 'buy';
  };
  // Team drivers: a truck can hold 2 drivers as one team. `driver` stays = drivers[0]
  // for backward compatibility with single-driver rendering paths.
  drivers?: Array<{
    id: string;
    first_name: string;
    last_name: string;
    driver_type?: 'company_driver' | 'owner_operator' | 'rent' | 'buy';
  }>;
  team_id?: string | null;
  team_name?: string | null;
  dispatcher?: {
    id: string;
    first_name: string;
    last_name: string;
    full_name?: string;
  };
}

interface Driver {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
  is_active: boolean;
}

interface Dispatcher {
  id: string;
  email: string;
  full_name: string;
}

interface FleetViewProps {
  companyFilter?: string;
}

export function FleetView({ companyFilter }: FleetViewProps = {}) {
  const toast = useToast();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [trucks, setTrucks] = useState<TruckData[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [showUnassignConfirm, setShowUnassignConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [truckToAction, setTruckToAction] = useState<TruckData | null>(null);
  const [selectedTruck, setSelectedTruck] = useState<TruckData | null>(null);
  const [assigningDriver, setAssigningDriver] = useState(false);
  const [assignedDriverIds, setAssignedDriverIds] = useState<Set<string>>(new Set());
  // Team-assignment mode in the Assign Driver modal
  const [teamMode, setTeamMode] = useState(false);
  const [selectedTeamDriverIds, setSelectedTeamDriverIds] = useState<string[]>([]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedTruckForStatus, setSelectedTruckForStatus] = useState<TruckData | null>(null);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [collapsedStatuses, setCollapsedStatuses] = useState<Set<string>>(new Set([
    'ACTIVE',
    'ALERT',
    'POTENCIJALNI RECOVERY',
    'RECOVERY - 100%',
    'COMING BACK',
    'JARDA - AVAILABLE',
    'JARDA - VACATION',
    'SHOP/NA YARDI - nasi kamioni',
    'SHOP OWNERI',
    'VACATION - ALI NISU NA JARDI PARKIRANI',
    'INACTIVE'
  ]));
  const [collapsedCompanies, setCollapsedCompanies] = useState<Set<string>>(new Set());
  const [selectedTruckIds, setSelectedTruckIds] = useState<Set<string>>(new Set());
  const [showMoveToModal, setShowMoveToModal] = useState(false);
  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [trailerInput, setTrailerInput] = useState('');
  const [trailers, setTrailers] = useState<any[]>([]);
  const [driverSearchTerm, setDriverSearchTerm] = useState('');
  const [dispatcherSearchTerm, setDispatcherSearchTerm] = useState('');
  const [trailerSearch, setTrailerSearch] = useState('');
  const [showTrailerDropdown, setShowTrailerDropdown] = useState(false);
  const [showDriverStatusModal, setShowDriverStatusModal] = useState(false);
  const [selectedTruckForDriverStatus, setSelectedTruckForDriverStatus] = useState<TruckData | null>(null);
  const [showDispatcherModal, setShowDispatcherModal] = useState(false);
  const [showUnassignDispatcherConfirm, setShowUnassignDispatcherConfirm] = useState(false);
  const [selectedTruckForDispatcher, setSelectedTruckForDispatcher] = useState<TruckData | null>(null);
  const [dispatchers, setDispatchers] = useState<Dispatcher[]>([]);
  const [assigningDispatcher, setAssigningDispatcher] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedTruckForNote, setSelectedTruckForNote] = useState<TruckData | null>(null);
  const [noteInput, setNoteInput] = useState('');
  const [noteAttachment, setNoteAttachment] = useState<File | null>(null);
  const [noteAttachmentUrl, setNoteAttachmentUrl] = useState<string>('');
  const [savingNote, setSavingNote] = useState(false);
  const [driverStatusFilter, setDriverStatusFilter] = useState<string[]>([]);

  const [companyNames, setCompanyNames] = useState<Record<string, string>>({});
  const [companies, setCompanies] = useState<Array<{id: string; name: string; code: string}>>([]);

  // Load company names from database
  useEffect(() => {
    async function loadCompanyNames() {
      const { data } = await supabase
        .from('companies')
        .select('id, code, name')
        .eq('is_active', true);
      
      if (data) {
        setCompanies(data);
        const names: Record<string, string> = {};
        data.forEach(company => {
          names[company.code] = company.name;
          names[company.code.toLowerCase()] = company.name;
        });
        setCompanyNames(names);
      }
    }
    loadCompanyNames();
  }, []);

  useEffect(() => {
    loadTrucks();
  }, [companyFilter]);

  async function loadTrucks() {
    try {
      setLoading(true);
      
      let companyId = null;
      if (companyFilter) {
        const { data: company } = await supabase
          .from('companies')
          .select('id')
          .eq('code', companyFilter)
          .single();
        companyId = company?.id;
      }

      // Try the team-aware query first; if the team tables/columns don't exist yet
      // (migration not applied), fall back to the original driver-only query so the
      // fleet still loads normally.
      let assignments: any[] | null = null;
      {
        const teamAware = await supabase
          .from('equipment_assignments')
          .select('truck_id, driver_id, team_id, drivers(id, first_name, last_name, driver_status, driver_type), driver_teams(id, name, is_active)')
          .eq('is_current', true);
        if (teamAware.error) {
          const fallback = await supabase
            .from('equipment_assignments')
            .select('truck_id, driver_id, drivers(id, first_name, last_name, driver_status, driver_type)')
            .eq('is_current', true);
          assignments = fallback.data as any[] | null;
        } else {
          assignments = teamAware.data as any[] | null;
        }
      }

      let trucksQuery = supabase
        .from('trucks')
        .select('*, companies(id, name, code)')
        .order('truck_status', { ascending: true })
        .order('unit_number', { ascending: true });
      
      if (companyId) {
        trucksQuery = trucksQuery.eq('company_id', companyId);
      }

      const { data: trucksData, error: trucksError } = await trucksQuery;
      const { data: driversData } = await supabase
        .from('drivers')
        .select('*')
        .neq('driver_status', 'terminated')
        .order('last_name');

      const { data: dispatchersData, error: dispatchersError } = await supabase
        .from('profiles')
        .select('id, email, full_name')
        .in('role', ['Dispatch', 'Dispatch Manager'])
        .order('full_name');

      if (dispatchersError) {
        console.error('Error loading dispatchers:', dispatchersError);
      }

      // Load trailers
      const { data: trailersData, error: trailersError } = await supabase
        .from('trailers')
        .select('id, unit_number, make, model, year')
        .order('unit_number');

      if (trailersError) {
        console.error('Error loading trailers:', trailersError);
      }

      if (trucksError) throw trucksError;

      // Define custom status order
      const statusOrder = [
        'ACTIVE',
        'ALERT',
        'POTENCIJALNI RECOVERY',
        'RECOVERY - 100%',
        'COMING BACK',
        'JARDA - AVAILABLE',
        'JARDA - VACATION',
        'SHOP/NA YARDI - nasi kamioni',
        'SHOP OWNERI',
        'VACATION - ALI NISU NA JARDI PARKIRANI',
        'INACTIVE'
      ];

      const trucksWithDrivers = (trucksData || []).map(truck => {
        // A team-assigned truck has 2 current assignment rows (one per member);
        // a solo truck has 1. Collect ALL of them so the truck can show both drivers.
        const truckAssignments = (assignments || []).filter(a => a.truck_id === truck.id);
        const driverList = truckAssignments.map(a => a.drivers).filter(Boolean) as any[];
        const teamRow = truckAssignments.find(a => (a as any).team_id)?.driver_teams as any;
        const dispatcher = dispatchersData?.find(d => d.id === truck.dispatcher_id);
        return {
          ...truck,
          drivers: driverList,
          driver: driverList[0] as any, // back-compat: existing single-driver renders
          team_id: teamRow?.id ?? null,
          team_name: teamRow?.name ?? null,
          dispatcher: dispatcher as any
        };
      }).sort((a, b) => {
        // Sort by status order first
        const statusA = statusOrder.indexOf(a.truck_status);
        const statusB = statusOrder.indexOf(b.truck_status);
        
        if (statusA !== statusB) {
          return statusA - statusB;
        }
        
        // Then by unit number
        return a.unit_number.localeCompare(b.unit_number, undefined, { numeric: true });
      });

      const assignedIds = new Set(assignments?.map(a => a.driver_id).filter(Boolean) || []);
      setAssignedDriverIds(assignedIds);

      setTrucks(trucksWithDrivers);
      setDrivers(driversData || []);
      setDispatchers(dispatchersData || []);
      setTrailers(trailersData || []);
    } catch (error) {
      console.error('Error loading trucks:', error);
      toast.error('Failed to load trucks');
    } finally {
      setLoading(false);
    }
  }

  const statusConfig = {
    'ACTIVE': {
      label: 'ACTIVE',
      description: 'Truck is actively in service',
      color: 'bg-green-100 text-green-700'
    },
    'ALERT': {
      label: 'ALERT',
      description: 'ALERT = FC, ne voze, shop na putu, medical itd.',
      color: 'bg-red-100 text-red-700'
    },
    'POTENCIJALNI RECOVERY': {
      label: 'POTENCIJALNI RECOVERY',
      description: 'Potential recovery situation',
      color: 'bg-yellow-100 text-yellow-700'
    },
    'RECOVERY - 100%': {
      label: 'RECOVERY - 100%',
      description: 'Full recovery mode',
      color: 'bg-orange-100 text-orange-700'
    },
    'COMING BACK': {
      label: 'COMING BACK',
      description: 'Truck is returning',
      color: 'bg-blue-100 text-blue-700'
    },
    'JARDA - AVAILABLE': {
      label: 'JARDA - AVAILABLE',
      description: 'Available at yard',
      color: 'bg-cyan-100 text-cyan-700'
    },
    'JARDA - VACATION': {
      label: 'JARDA - VACATION',
      description: 'Parked at yard for vacation',
      color: 'bg-purple-100 text-purple-700'
    },
    'SHOP/NA YARDI - nasi kamioni': {
      label: 'SHOP/NA YARDI - nasi kamioni',
      description: 'Our trucks in shop or at yard',
      color: 'bg-indigo-100 text-indigo-700'
    },
    'SHOP OWNERI': {
      label: 'SHOP OWNERI',
      description: 'Owner trucks in shop',
      color: 'bg-pink-100 text-pink-700'
    },
    'VACATION - ALI NISU NA JARDI PARKIRANI': {
      label: 'VACATION - ALI NISU NA JARDI PARKIRANI',
      description: 'On vacation but not parked at yard',
      color: 'bg-amber-100 text-amber-700'
    },
    'INACTIVE': {
      label: 'INACTIVE',
      description: 'Truck is not in service',
      color: 'bg-gray-100 text-gray-700'
    }
  };

  const filteredTrucks = trucks.filter(truck => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      truck.unit_number.toLowerCase().includes(searchLower) ||
      truck.vin?.toLowerCase().includes(searchLower) ||
      truck.license_plate?.toLowerCase().includes(searchLower) ||
      truck.make?.toLowerCase().includes(searchLower) ||
      truck.model?.toLowerCase().includes(searchLower) ||
      ((truck.drivers && truck.drivers.length > 0 ? truck.drivers : truck.driver ? [truck.driver] : [])
        .some(d => `${d.first_name} ${d.last_name}`.toLowerCase().includes(searchLower)));
    
    const matchesDriverStatus = driverStatusFilter.length === 0 || 
      driverStatusFilter.includes(truck.driver_status);
    
    return matchesSearch && matchesDriverStatus;
  });

  const trucksByStatus = companyFilter ? filteredTrucks.reduce((acc, truck) => {
    const status = truck.truck_status || 'ACTIVE';
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(truck);
    return acc;
  }, {} as Record<string, TruckData[]>) : {};

  const trucksByCompany = !companyFilter ? filteredTrucks.reduce((acc, truck) => {
    const companyCode = truck.companies?.code || 'UNASSIGNED';
    if (!acc[companyCode]) {
      acc[companyCode] = [];
    }
    acc[companyCode].push(truck);
    return acc;
  }, {} as Record<string, TruckData[]>) : {};

  const sortedStatuses = companyFilter 
    ? ['ACTIVE', 'ALERT', 'POTENCIJALNI RECOVERY', 'RECOVERY - 100%', 'COMING BACK', 'JARDA - AVAILABLE', 'JARDA - VACATION', 'SHOP/NA YARDI - nasi kamioni', 'SHOP OWNERI', 'VACATION - ALI NISU NA JARDI PARKIRANI', 'INACTIVE']
    : [];

  const sortedCompanies = !companyFilter ? Object.keys(trucksByCompany).sort((a, b) => {
    // Sort by company name, with UNASSIGNED always last
    if (a === 'UNASSIGNED') return 1;
    if (b === 'UNASSIGNED') return -1;
    return a.localeCompare(b);
  }) : [];

  const toggleTruckSelection = (truckId: string) => {
    setSelectedTruckIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(truckId)) {
        newSet.delete(truckId);
      } else {
        newSet.add(truckId);
      }
      return newSet;
    });
  };

  const toggleStatusCollapse = (status: string) => {
    setCollapsedStatuses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleCompanyCollapse = (companyCode: string) => {
    setCollapsedCompanies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(companyCode)) {
        newSet.delete(companyCode);
      } else {
        newSet.add(companyCode);
      }
      return newSet;
    });
  };

  const handleBatchDelete = async () => {
    if (selectedTruckIds.size === 0) return;
    
    try {
      const { error } = await supabase
        .from('trucks')
        .delete()
        .in('id', Array.from(selectedTruckIds));
      
      if (error) throw error;
      
      toast.success(`${selectedTruckIds.size} truck(s) deleted successfully!`);
      setSelectedTruckIds(new Set());
      loadTrucks();
    } catch (error) {
      console.error('Error deleting trucks:', error);
      toast.error('Failed to delete trucks');
    }
  };

  const handleBatchStatusChange = async (newStatus: string) => {
    if (selectedTruckIds.size === 0) return;

    try {
      const { error } = await supabase
        .from('trucks')
        .update({ truck_status: newStatus })
        .in('id', Array.from(selectedTruckIds));

      if (error) throw error;

      toast.success(`Updated ${selectedTruckIds.size} truck(s) status to ${statusConfig[newStatus as keyof typeof statusConfig]?.label}`);
      setSelectedTruckIds(new Set());
      setShowMoveToModal(false);
      loadTrucks();
    } catch (error) {
      console.error('Error updating truck status:', error);
      toast.error('Failed to update truck status');
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!selectedTruckForStatus) return;

    try {
      const { error } = await supabase
        .from('trucks')
        .update({ truck_status: newStatus })
        .eq('id', selectedTruckForStatus.id);

      if (error) throw error;

      toast.success('Truck status updated successfully!');
      setShowStatusModal(false);
      setSelectedTruckForStatus(null);
      loadTrucks();
    } catch (error) {
      console.error('Error updating truck status:', error);
      toast.error('Failed to update truck status');
    }
  };

  // Deactivate a team and remove its memberships (so the single-active-team
  // trigger won't block future assignments and the team disappears from pickers).
  const teardownTeam = async (teamId: string) => {
    const now = new Date().toISOString();
    await supabase.from('driver_teams').update({ is_active: false, updated_at: now }).eq('id', teamId);
    await supabase.from('driver_team_members').delete().eq('team_id', teamId);
  };

  const closeDriverModal = () => {
    setShowDriverModal(false);
    setSelectedTruck(null);
    setTeamMode(false);
    setSelectedTeamDriverIds([]);
    setDriverSearchTerm('');
  };

  const toggleTeamDriver = (driverId: string) => {
    setSelectedTeamDriverIds(prev => {
      if (prev.includes(driverId)) return prev.filter(id => id !== driverId);
      if (prev.length >= 2) return prev; // cap at 2
      return [...prev, driverId];
    });
  };

  const handleAssignDriver = async (driverId: string) => {
    if (!selectedTruck) return;

    setAssigningDriver(true);
    try {
      await supabase
        .from('equipment_assignments')
        .update({ is_current: false, released_at: new Date().toISOString() })
        .eq('driver_id', driverId)
        .eq('is_current', true);

      // Release whoever is currently on this truck (single OR team -> both rows)
      await supabase
        .from('equipment_assignments')
        .update({ is_current: false, released_at: new Date().toISOString() })
        .eq('truck_id', selectedTruck.id)
        .eq('is_current', true);

      // If the truck held a team, tear that team down before assigning a solo driver
      if (selectedTruck.team_id) {
        await teardownTeam(selectedTruck.team_id);
      }

      const { error } = await supabase
        .from('equipment_assignments')
        .insert({
          driver_id: driverId,
          truck_id: selectedTruck.id,
          is_current: true,
          assigned_at: new Date().toISOString(),
        });

      if (error) throw error;

      toast.success('Driver assigned successfully!');
      closeDriverModal();
      loadTrucks();
    } catch (error) {
      console.error('Error assigning driver:', error);
      toast.error('Failed to assign driver');
    } finally {
      setAssigningDriver(false);
    }
  };

  const handleAssignTeam = async () => {
    if (!selectedTruck || selectedTeamDriverIds.length !== 2) return;

    setAssigningDriver(true);
    try {
      const now = new Date().toISOString();

      // 1. Release each chosen driver's current assignment (solo or prior team)
      await supabase
        .from('equipment_assignments')
        .update({ is_current: false, released_at: now })
        .in('driver_id', selectedTeamDriverIds)
        .eq('is_current', true);

      // 2. Release whoever is currently on this truck
      await supabase
        .from('equipment_assignments')
        .update({ is_current: false, released_at: now })
        .eq('truck_id', selectedTruck.id)
        .eq('is_current', true);

      // 3. Deactivate + clear any prior ACTIVE team either driver belonged to,
      //    so the single-active-team trigger won't reject the new memberships.
      const { data: priorMembers } = await supabase
        .from('driver_team_members')
        .select('team_id, driver_teams!inner(is_active)')
        .in('driver_id', selectedTeamDriverIds)
        .eq('driver_teams.is_active', true);
      const priorTeamIds = [...new Set((priorMembers || []).map((r: any) => r.team_id))];
      if (selectedTruck.team_id && !priorTeamIds.includes(selectedTruck.team_id)) {
        priorTeamIds.push(selectedTruck.team_id);
      }
      for (const tid of priorTeamIds) {
        await teardownTeam(tid as string);
      }

      // 4. Create the team
      const d1 = drivers.find(d => d.id === selectedTeamDriverIds[0]);
      const d2 = drivers.find(d => d.id === selectedTeamDriverIds[1]);
      const teamName = `${d1?.last_name ?? ''} / ${d2?.last_name ?? ''}`.trim();
      const { data: team, error: teamErr } = await supabase
        .from('driver_teams')
        .insert({ name: teamName, is_active: true })
        .select('id')
        .single();
      if (teamErr) throw teamErr;

      // 5. Members
      const { error: memErr } = await supabase
        .from('driver_team_members')
        .insert(selectedTeamDriverIds.map(driver_id => ({ team_id: team.id, driver_id })));
      if (memErr) throw memErr;

      // 6. Two equipment_assignments rows, both tagged with team_id
      const { error: eaErr } = await supabase
        .from('equipment_assignments')
        .insert(selectedTeamDriverIds.map(driver_id => ({
          driver_id,
          truck_id: selectedTruck.id,
          team_id: team.id,
          is_current: true,
          assigned_at: now,
        })));
      if (eaErr) throw eaErr;

      toast.success('Team assigned successfully!');
      closeDriverModal();
      loadTrucks();
    } catch (error) {
      console.error('Error assigning team:', error);
      toast.error('Failed to assign team');
    } finally {
      setAssigningDriver(false);
    }
  };

  const handleUnassignDriver = async () => {
    if (!truckToAction) return;

    try {
      const { error } = await supabase
        .from('equipment_assignments')
        .update({ is_current: false, released_at: new Date().toISOString() })
        .eq('truck_id', truckToAction.id)
        .eq('is_current', true);

      if (error) throw error;

      // Tear down the team if this truck held one
      if (truckToAction.team_id) {
        await teardownTeam(truckToAction.team_id);
      }

      toast.success(truckToAction.team_id ? 'Team unassigned successfully!' : 'Driver unassigned successfully!');
      setShowUnassignConfirm(false);
      setTruckToAction(null);
      loadTrucks();
    } catch (error) {
      console.error('Error unassigning driver:', error);
      toast.error('Failed to unassign driver');
    }
  };

  const handleAssignTrailer = async (nextTrailerInput?: string) => {
    if (!selectedTruck) return;

    try {
      const resolvedTrailerInput = nextTrailerInput !== undefined ? nextTrailerInput : trailerInput;
      const { error } = await supabase
        .from('trucks')
        .update({ trailers: parseTrailers(resolvedTrailerInput) })
        .eq('id', selectedTruck.id);

      if (error) throw error;

      toast.success(resolvedTrailerInput ? 'Trailer assigned successfully!' : 'Trailer removed successfully!');
      setShowTrailerModal(false);
      setSelectedTruck(null);
      setTrailerInput('');
      loadTrucks();
    } catch (error) {
      console.error('Error updating trailer:', error);
      toast.error('Failed to update trailer');
    }
  };

  const handleDriverStatusChange = async (newDriverStatus: 'OWNER' | 'COMPANY' | 'RENT' | 'BUYING' | 'UNASSIGNED') => {
    if (!selectedTruckForDriverStatus) return;

    try {
      const { error } = await supabase
        .from('trucks')
        .update({ driver_status: newDriverStatus })
        .eq('id', selectedTruckForDriverStatus.id);

      if (error) throw error;

      toast.success(`Driver status updated to ${newDriverStatus}`);
      setShowDriverStatusModal(false);
      setSelectedTruckForDriverStatus(null);
      loadTrucks();
    } catch (error) {
      console.error('Error updating driver status:', error);
      toast.error('Failed to update driver status');
    }
  };

  const handleToggleSamsara = async (truck: TruckData) => {
    try {
      // Cycle through: Yes -> No -> Prijavljena -> Yes
      let newSamsaraStatus: 'Yes' | 'No' | 'Prijavljena';
      if (truck.samsara_enabled === 'Yes') {
        newSamsaraStatus = 'No';
      } else if (truck.samsara_enabled === 'No') {
        newSamsaraStatus = 'Prijavljena';
      } else {
        newSamsaraStatus = 'Yes';
      }

      const { error } = await supabase
        .from('trucks')
        .update({ samsara_enabled: newSamsaraStatus })
        .eq('id', truck.id);

      if (error) throw error;

      toast.success(`Samsara: ${newSamsaraStatus}`);
      loadTrucks();
    } catch (error) {
      console.error('Error toggling Samsara:', error);
      toast.error('Failed to update Samsara status');
    }
  };

  const handleAssignDispatcher = async (dispatcherId: string) => {
    if (!selectedTruckForDispatcher) return;
    setAssigningDispatcher(true);
    try {
      const nowIso = new Date().toISOString();

      // Close current assignment row so dispatcher change is historically tracked
      const { data: currentAssignment } = await supabase
        .from('equipment_assignments')
        .select('id, driver_id, truck_id, trailer_id')
        .eq('truck_id', selectedTruckForDispatcher.id)
        .eq('is_current', true)
        .maybeSingle();

      if (currentAssignment?.id) {
        await supabase
          .from('equipment_assignments')
          .update({ is_current: false, released_at: nowIso })
          .eq('id', currentAssignment.id);

        await supabase
          .from('equipment_assignments')
          .insert({
            driver_id: currentAssignment.driver_id,
            truck_id: currentAssignment.truck_id,
            trailer_id: currentAssignment.trailer_id,
            is_current: true,
            assigned_at: nowIso,
          });
      }

      const { error } = await supabase
        .from('trucks')
        .update({ dispatcher_id: dispatcherId })
        .eq('id', selectedTruckForDispatcher.id);
      
      if (error) throw error;
      
      toast.success('Dispatcher assigned successfully!');
      setShowDispatcherModal(false);
      setSelectedTruckForDispatcher(null);
      loadTrucks();
    } catch (error) {
      console.error('Error assigning dispatcher:', error);
      toast.error('Failed to assign dispatcher');
    } finally {
      setAssigningDispatcher(false);
    }
  };

  const handleUnassignDispatcher = async () => {
    if (!selectedTruckForDispatcher) return;
    try {
      const nowIso = new Date().toISOString();

      // First, get the driver assigned to this truck
      const { data: assignment } = await supabase
        .from('equipment_assignments')
        .select('id, driver_id, truck_id, trailer_id')
        .eq('truck_id', selectedTruckForDispatcher.id)
        .eq('is_current', true)
        .single();

      // Close current assignment row and create new one with dispatcher_id null
      if (assignment?.id) {
        await supabase
          .from('equipment_assignments')
          .update({ is_current: false, released_at: nowIso })
          .eq('id', assignment.id);

        await supabase
          .from('equipment_assignments')
          .insert({
            driver_id: assignment.driver_id,
            truck_id: assignment.truck_id,
            trailer_id: assignment.trailer_id,
            is_current: true,
            assigned_at: nowIso,
          });
      }

      // Unassign dispatcher from truck
      const { error: truckError } = await supabase
        .from('trucks')
        .update({ dispatcher_id: null })
        .eq('id', selectedTruckForDispatcher.id);
      
      if (truckError) throw truckError;

      // If there's a driver assigned to this truck, also clear their dispatcher_id
      if (assignment?.driver_id) {
        const { error: driverError } = await supabase
          .from('drivers')
          .update({ dispatcher_id: null })
          .eq('id', assignment.driver_id);
        
        if (driverError) {
          console.error('Error clearing driver dispatcher:', driverError);
          // Don't throw - truck dispatcher was cleared successfully
        }
      }
      
      toast.success('Dispatcher unassigned successfully!');
      setShowUnassignDispatcherConfirm(false);
      setSelectedTruckForDispatcher(null);
      loadTrucks();
    } catch (error) {
      console.error('Error unassigning dispatcher:', error);
      toast.error('Failed to unassign dispatcher');
    }
  };

  const handleSaveNote = async () => {
    if (!selectedTruckForNote) return;
    
    setSavingNote(true);
    try {
      let attachmentUrl = noteAttachmentUrl;

      // Upload file if a new one is selected
      if (noteAttachment) {
        const fileExt = noteAttachment.name.split('.').pop();
        const fileName = `note_${selectedTruckForNote.unit_number}_${Date.now()}.${fileExt}`;
        const filePath = `truck-notes/${selectedTruckForNote.unit_number}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('company-documents')
          .upload(filePath, noteAttachment, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('company-documents')
          .getPublicUrl(filePath);

        attachmentUrl = publicUrl;
      }

      const { error } = await supabase
        .from('trucks')
        .update({ 
          notes: noteInput || null,
          note_attachment_url: attachmentUrl || null
        })
        .eq('id', selectedTruckForNote.id);
      
      if (error) throw error;

      toast.success('Note saved successfully!');
      setShowNoteModal(false);
      setSelectedTruckForNote(null);
      setNoteInput('');
      setNoteAttachment(null);
      setNoteAttachmentUrl('');
      loadTrucks();
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  // Placeholder for rest of dispatcher handlers
  //     setAssigningDispatcher(false);
  //   }
  // };
  // const handleUnassignDispatcher = async () => {
  //   if (!selectedTruckForDispatcher) return;
  //   try {
  //     const { error } = await supabase.from('trucks').update({ dispatcher_id: null }).eq('id', selectedTruckForDispatcher.id);
  //     if (error) throw error;
  //     toast.success('Dispatcher unassigned successfully!');
  //     setShowUnassignDispatcherConfirm(false);
  //     setSelectedTruckForDispatcher(null);
  //     loadTrucks();
  //   } catch (error) {
  //     console.error('Error unassigning dispatcher:', error);
  //     toast.error('Failed to unassign dispatcher');
  //   }
  // };

  const handleDeleteTruck = async () => {
    if (!truckToAction) return;

    try {
      if (truckToAction.driver) {
        await supabase
          .from('equipment_assignments')
          .update({ is_current: false, released_at: new Date().toISOString() })
          .eq('truck_id', truckToAction.id)
          .eq('is_current', true);
      }

      const { error } = await supabase
        .from('trucks')
        .delete()
        .eq('id', truckToAction.id);

      if (error) throw error;

      toast.success('Truck deleted successfully!');
      setShowDeleteConfirm(false);
      setTruckToAction(null);
      loadTrucks();
    } catch (error: any) {
      console.error('Error deleting truck:', error);
      toast.error('Failed to delete truck');
    }
  };

  const isExpiringSoon = (date: string) => {
    if (!date) return false;
    const expirationDate = new Date(date);
    const today = new Date();
    const daysUntilExpiration = Math.ceil((expirationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiration <= 30 && daysUntilExpiration > 0;
  };

  const isExpired = (date: string) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Loading trucks...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {companyFilter ? `${companyNames[companyFilter.toUpperCase()] || companyNames[companyFilter] || 'Company'} Trucks` : 'All Trucks'}
          </h1>
          <p className="text-slate-600 mt-1">{filteredTrucks.length} trucks</p>
        </div>
        <button
          onClick={() => navigate('/fleet/create')}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Truck
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search trucks by unit number, VIN, license plate, driver..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Filter by Driver Status</label>
          <div className="flex flex-wrap gap-2">
            {['OWNER', 'COMPANY', 'RENT', 'BUYING', 'UNASSIGNED'].map((status) => {
              const isActive = driverStatusFilter.includes(status);
              return (
                <button
                  key={status}
                  onClick={() => {
                    setDriverStatusFilter(prev => 
                      isActive 
                        ? prev.filter(s => s !== status)
                        : [...prev, status]
                    );
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? status === 'OWNER' ? 'bg-green-600 text-white hover:bg-green-700'
                      : status === 'COMPANY' ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : status === 'RENT' ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : status === 'BUYING' ? 'bg-orange-600 text-white hover:bg-orange-700'
                      : 'bg-gray-600 text-white hover:bg-gray-700'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {status}
                </button>
              );
            })}
            {driverStatusFilter.length > 0 && (
              <button
                onClick={() => setDriverStatusFilter([])}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <tbody>
              {companyFilter && sortedStatuses.map((status) => {
                const statusTrucks = trucksByStatus[status] || [];
                const isCollapsed = collapsedStatuses.has(status);
                
                return (
                  <React.Fragment key={status}>
                    <tr>
                      <td colSpan={16} className="p-0">
                        <div 
                          className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-all duration-200 ${
                            status === 'ACTIVE' 
                              ? 'bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border-green-500'
                              : status === 'ALERT'
                              ? 'bg-gradient-to-r from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 border-red-500'
                              : status === 'POTENCIJALNI RECOVERY'
                              ? 'bg-gradient-to-r from-yellow-50 to-yellow-100 hover:from-yellow-100 hover:to-yellow-200 border-yellow-500'
                              : status === 'RECOVERY - 100%'
                              ? 'bg-gradient-to-r from-orange-50 to-orange-100 hover:from-orange-100 hover:to-orange-200 border-orange-500'
                              : status === 'COMING BACK'
                              ? 'bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border-blue-500'
                              : status === 'JARDA - AVAILABLE'
                              ? 'bg-gradient-to-r from-cyan-50 to-cyan-100 hover:from-cyan-100 hover:to-cyan-200 border-cyan-500'
                              : status === 'JARDA - VACATION'
                              ? 'bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 border-purple-500'
                              : status === 'SHOP/NA YARDI - nasi kamioni'
                              ? 'bg-gradient-to-r from-indigo-50 to-indigo-100 hover:from-indigo-100 hover:to-indigo-200 border-indigo-500'
                              : status === 'SHOP OWNERI'
                              ? 'bg-gradient-to-r from-pink-50 to-pink-100 hover:from-pink-100 hover:to-pink-200 border-pink-500'
                              : status === 'VACATION - ALI NISU NA JARDI PARKIRANI'
                              ? 'bg-gradient-to-r from-amber-50 to-amber-100 hover:from-amber-100 hover:to-amber-200 border-amber-500'
                              : status === 'INACTIVE'
                              ? 'bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 border-gray-400'
                              : 'bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-100 hover:to-slate-200 border-slate-400'
                          } border-l-4`}
                          onClick={() => toggleStatusCollapse(status)}
                        >
                          <div>
                            <h3 className="font-bold text-slate-900 text-base">
                              {statusConfig[status as keyof typeof statusConfig]?.label || status}
                            </h3>
                            <p className="text-xs text-slate-600">{statusTrucks.length} trucks</p>
                          </div>
                          <div className={`transform transition-transform duration-200 ${isCollapsed ? '' : 'rotate-180'}`}>
                            <ChevronDown className="w-5 h-5 text-slate-600" />
                          </div>
                        </div>
                      </td>
                    </tr>

                    {!isCollapsed && statusTrucks.length > 0 && (
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-3 py-2 text-left">
                          <input
                            type="checkbox"
                            onChange={() => {
                              const statusTruckIds = statusTrucks.map(t => t.id);
                              const allSelected = statusTruckIds.every(id => selectedTruckIds.has(id));
                              setSelectedTruckIds(prev => {
                                const newSet = new Set(prev);
                                if (allSelected) {
                                  statusTruckIds.forEach(id => newSet.delete(id));
                                } else {
                                  statusTruckIds.forEach(id => newSet.add(id));
                                }
                                return newSet;
                              });
                            }}
                            className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                          />
                        </th>
                        <th onClick={() => handleSort('unit_number')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100">Truck {sortField === 'unit_number' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('trailers')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100">Trailer {sortField === 'trailers' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('driver')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100">Driver {sortField === 'driver' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('notes')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100">Note {sortField === 'notes' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('dispatcher')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100">Dispatcher {sortField === 'dispatcher' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('driver_status')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100">Driver Status {sortField === 'driver_status' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('samsara_enabled')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100">Samsara {sortField === 'samsara_enabled' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('vin')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100">VIN {sortField === 'vin' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('prepass')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100">Prepass {sortField === 'prepass' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('license_plate')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100">License Plate {sortField === 'license_plate' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('make')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100">Make {sortField === 'make' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('year')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100">Year {sortField === 'year' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('registration_expiration')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100">Reg Exp {sortField === 'registration_expiration' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">IFTA</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
                      </tr>
                    )}

                    {!isCollapsed && statusTrucks.sort((a, b) => {
                      if (!sortField) return 0;
                      
                      let aVal: any = sortField === 'driver' 
                        ? (a.driver ? `${a.driver.first_name} ${a.driver.last_name}` : '')
                        : (a as any)[sortField];
                      let bVal: any = sortField === 'driver'
                        ? (b.driver ? `${b.driver.first_name} ${b.driver.last_name}` : '')
                        : (b as any)[sortField];
                      
                      if (aVal === null || aVal === undefined) aVal = '';
                      if (bVal === null || bVal === undefined) bVal = '';
                      
                      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
                      
                      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
                      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
                      return 0;
                    }).map((truck) => {
                      const regExpiring = isExpiringSoon(truck.registration_expiration);
                      const regExpired = isExpired(truck.registration_expiration);

                      return (
                        <tr 
                          key={truck.id} 
                          className={`border-b border-slate-200 ${
                            selectedTruckIds.has(truck.id) ? 'bg-blue-50' : ''
                          }`}
                          style={{height: '36px'}}
                        >
                          <td className="px-3 py-1" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedTruckIds.has(truck.id)}
                              onChange={() => toggleTruckSelection(truck.id)}
                              className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-3 py-1">
                            <button
                              onClick={() => navigate(`/fleet/${truck.id}`)}
                              className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-sm transition-colors"
                            >
                              {truck.unit_number}
                            </button>
                          </td>
                          <td 
                            className="px-3 py-1 cursor-pointer hover:bg-blue-100 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTruck(truck);
                              setTrailerInput(formatTrailers(truck.trailers));
                              setTrailerSearch('');
                              setShowTrailerDropdown(false);
                              setShowTrailerModal(true);
                            }}
                            title="Click to assign/update trailer"
                          >
                            <span className="text-slate-700 text-xs">{formatTrailers(truck.trailers) || '-'}</span>
                          </td>
                          <td 
                            className="px-3 py-1 cursor-pointer hover:bg-blue-100 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (truck.driver) {
                                setTruckToAction(truck);
                                setShowUnassignConfirm(true);
                              } else {
                                setSelectedTruck(truck);
                                setShowDriverModal(true);
                              }
                            }}
                            title={truck.driver ? 'Click to unassign driver' : 'Click to assign driver'}
                          >
                            {truck.drivers && truck.drivers.length > 1 ? (
                              <div className="flex flex-col gap-0.5">
                                <span className="inline-flex items-center gap-1">
                                  <span className="text-[10px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">TEAM</span>
                                </span>
                                {truck.drivers.map(d => (
                                  <span key={d.id} className="text-slate-900 text-sm">{d.first_name} {d.last_name}</span>
                                ))}
                              </div>
                            ) : truck.driver ? (
                              <span className="text-slate-900 text-sm">{truck.driver.first_name} {truck.driver.last_name}</span>
                            ) : (
                              <span className="text-slate-400 text-xs italic">No driver</span>
                            )}
                          </td>
                          <td
                            className="px-3 py-1 cursor-pointer hover:bg-blue-100 transition-colors relative group"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTruckForNote(truck);
                              setNoteInput((truck as any).notes || '');
                              setNoteAttachmentUrl((truck as any).note_attachment_url || '');
                              setNoteAttachment(null);
                              setShowNoteModal(true);
                            }}
                            style={{ maxWidth: '100px' }}
                          >
                            <div className="flex items-center gap-1">
                              <div className="text-slate-700 text-xs flex-1 truncate">
                                {(truck as any).notes 
                                  ? (truck as any).notes.length > 10 
                                    ? `${(truck as any).notes.substring(0, 10)}...` 
                                    : (truck as any).notes
                                  : '-'
                                }
                              </div>
                              {(truck as any).note_attachment_url && (
                                <svg className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                              )}
                            </div>
                            {(truck as any).notes && (truck as any).notes.length > 10 && (
                              <div className="absolute left-0 top-full mt-1 hidden group-hover:block z-50 pointer-events-none">
                                <div className="bg-slate-800 text-white text-xs rounded-lg shadow-2xl px-3 py-2 max-w-xs whitespace-pre-wrap">
                                  {(truck as any).notes}
                                  <div className="absolute -top-1 left-4 w-2 h-2 bg-slate-800 transform rotate-45"></div>
                                </div>
                              </div>
                            )}
                          </td>
                          <td 
                            className="px-3 py-1 cursor-pointer hover:bg-purple-100 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (truck.dispatcher) {
                                setSelectedTruckForDispatcher(truck);
                                setShowUnassignDispatcherConfirm(true);
                              } else {
                                setSelectedTruckForDispatcher(truck);
                                setShowDispatcherModal(true);
                              }
                            }}
                            title={truck.dispatcher ? 'Click to unassign dispatcher' : 'Click to assign dispatcher'}
                          >
                            {truck.dispatcher ? (
                              <span className="text-slate-900 text-sm">{truck.dispatcher.full_name}</span>
                            ) : (
                              <span className="text-slate-400 text-xs italic">No dispatcher</span>
                            )}
                          </td>
                          <td className="px-3 py-1">
                            {truck.driver?.driver_type ? (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                truck.driver.driver_type === 'owner_operator' ? 'bg-purple-100 text-purple-700' :
                                truck.driver.driver_type === 'company_driver' ? 'bg-blue-100 text-blue-700' :
                                truck.driver.driver_type === 'rent' ? 'bg-orange-100 text-orange-700' :
                                truck.driver.driver_type === 'buy' ? 'bg-green-100 text-green-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {truck.driver.driver_type === 'owner_operator' ? 'OWNER' :
                                 truck.driver.driver_type === 'company_driver' ? 'COMPANY' :
                                 truck.driver.driver_type === 'rent' ? 'RENT' :
                                 truck.driver.driver_type === 'buy' ? 'BUYING' :
                                 'OTHER'}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td 
                            className="px-3 py-1 cursor-pointer hover:bg-blue-100 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleSamsara(truck);
                            }}
                            title="Click to cycle: Yes → No → Prijavljena"
                          >
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              truck.samsara_enabled === 'Yes' ? 'bg-green-100 text-green-700' :
                              truck.samsara_enabled === 'No' ? 'bg-gray-100 text-gray-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {truck.samsara_enabled || 'No'}
                            </span>
                          </td>
                          <td className="px-3 py-1">
                            <span className="text-slate-600 text-xs font-mono">{truck.vin || '-'}</span>
                          </td>
                          <td className="px-3 py-1">
                            <span className="text-slate-700 text-xs">{truck.prepass || '-'}</span>
                          </td>
                          <td className="px-3 py-1">
                            <span className="text-slate-700 text-xs">{truck.license_plate || '-'}</span>
                          </td>
                          <td className="px-3 py-1">
                            <span className="text-slate-700 text-xs">{truck.make || '-'}</span>
                          </td>
                          <td className="px-3 py-1">
                            <span className="text-slate-700 text-xs">{truck.year || '-'}</span>
                          </td>
                          <td className="px-3 py-1">
                            <div className="flex items-center gap-1">
                              {regExpired && <AlertCircle className="w-3 h-3 text-red-600" />}
                              {regExpiring && !regExpired && <AlertCircle className="w-3 h-3 text-orange-600" />}
                              <span className={`text-xs ${
                                regExpired ? 'text-red-700 font-semibold' :
                                regExpiring ? 'text-orange-700 font-semibold' :
                                'text-slate-700'
                              }`}>
                                {formatDateNoTZ(truck.registration_expiration)}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-1">
                            {(() => {
                              const currentYear = new Date().getFullYear();
                              const iftaField = `ifta_${currentYear}` as keyof typeof truck;
                              const iftaValue = truck[iftaField];
                              return iftaValue && typeof iftaValue === 'string' && iftaValue.trim() !== '' ? (
                                <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                                  Yes
                                </span>
                              ) : (
                                <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">No</span>
                              );
                            })()}
                          </td>
                          <td className="px-0 py-0" style={{maxWidth: '120px'}}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTruckForStatus(truck);
                                setShowStatusModal(true);
                              }}
                              className={`w-full h-full flex items-center gap-1 px-3 py-2 text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${statusConfig[truck.truck_status as keyof typeof statusConfig]?.color || 'bg-green-100 text-green-700'}`}
                              title={statusConfig[truck.truck_status as keyof typeof statusConfig]?.description || 'Active'}
                            >
                              <Circle className="w-1.5 h-1.5 fill-current flex-shrink-0" />
                              <span className="truncate">{statusConfig[truck.truck_status as keyof typeof statusConfig]?.label || 'Active'}</span>
                            </button>
                          </td>
                          <td className="px-3 py-1" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-1">
                              {['Safety', 'Superadmin', 'Admin'].includes(profile?.role || '') && (
                                <>
                                  <button
                                    onClick={() => navigate(`/fleet/edit/${truck.id}`)}
                                    className="p-1 hover:bg-slate-200 rounded transition-colors"
                                    title="Edit truck"
                                  >
                                    <Edit className="w-3 h-3 text-slate-600" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setTruckToAction(truck);
                                      setShowDeleteConfirm(true);
                                    }}
                                    className="p-1 hover:bg-red-100 rounded transition-colors"
                                    title="Delete truck"
                                  >
                                    <Trash2 className="w-3 h-3 text-red-600" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}

              {!companyFilter && sortedCompanies.map((companyCode) => {
                const companyTrucks = trucksByCompany[companyCode];
                // Get company name from companies state or use code
                const company = companies.find(c => c.code === companyCode);
                const emojis = ['🚛', '⚡', '📦', '🚚', '🏢'];
                const companyIndex = companies.findIndex(c => c.code === companyCode);
                const emoji = companyIndex >= 0 ? emojis[companyIndex % emojis.length] : '📋';
                const companyName = companyCode === 'UNASSIGNED' ? 'Unassigned' : 
                                   company ? `${emoji} ${company.name}` : companyCode;
                const isCollapsed = collapsedCompanies.has(companyCode);
                
                return (
                  <React.Fragment key={companyCode}>
                    <tr>
                      <td colSpan={16} className="p-0">
                        <div 
                          className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-all duration-200 ${
                            companyCode === 'UNASSIGNED'
                              ? 'bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-100 hover:to-slate-200 border-l-4 border-slate-400'
                              : (() => {
                                  const colors = [
                                    'bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border-l-4 border-blue-500',
                                    'bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 border-l-4 border-purple-500',
                                    'bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border-l-4 border-green-500',
                                    'bg-gradient-to-r from-orange-50 to-orange-100 hover:from-orange-100 hover:to-orange-200 border-l-4 border-orange-500',
                                    'bg-gradient-to-r from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 border-l-4 border-red-500'
                                  ];
                                  const idx = companies.findIndex(c => c.code === companyCode);
                                  return idx >= 0 ? colors[idx % colors.length] : colors[0];
                                })()
                          }`}
                          onClick={() => toggleCompanyCollapse(companyCode)}
                        >
                          <div className="flex items-center gap-3">
                            <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                            <span className="text-lg font-semibold text-slate-900">{companyName}</span>
                            <span className="text-slate-600">{companyTrucks.length} trucks</span>
                          </div>
                        </div>
                      </td>
                    </tr>

                    {!isCollapsed && companyTrucks.length > 0 && (
                      <>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="px-3 py-2 text-left">
                            <input
                              type="checkbox"
                              className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                            />
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Truck</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Trailer</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Driver</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Driver Status</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Samsara</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">VIN</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Prepass</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">License Plate</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Model</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Year</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Reg Exp</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">DOT Exp</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">IFTA</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
                        </tr>
                        {companyTrucks.map((truck) => (
                          <tr 
                            key={truck.id} 
                            className="border-b border-slate-200"
                            style={{height: '36px'}}
                          >
                            <td className="px-3 py-1" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedTruckIds.has(truck.id)}
                                onChange={() => toggleTruckSelection(truck.id)}
                                className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-3 py-1">
                              <button
                                onClick={() => navigate(`/fleet/${truck.id}`)}
                                className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-sm transition-colors"
                              >
                                {truck.unit_number}
                              </button>
                            </td>
                            <td 
                              className="px-3 py-1 cursor-pointer hover:bg-blue-100 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTruck(truck);
                                setTrailerInput(formatTrailers(truck.trailers));
                                setTrailerSearch('');
                                setShowTrailerDropdown(false);
                                setShowTrailerModal(true);
                              }}
                              title="Click to assign/update trailer"
                            >
                              <span className="text-slate-700 text-xs">{formatTrailers(truck.trailers) || '-'}</span>
                            </td>
                            <td 
                              className="px-3 py-1 cursor-pointer hover:bg-blue-100 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (truck.driver) {
                                  setTruckToAction(truck);
                                  setShowUnassignConfirm(true);
                                } else {
                                  setSelectedTruck(truck);
                                  setShowDriverModal(true);
                                }
                              }}
                              title={truck.driver ? 'Click to unassign driver' : 'Click to assign driver'}
                            >
                              {truck.drivers && truck.drivers.length > 1 ? (
                                <div className="flex flex-col gap-0.5">
                                  <span className="text-[10px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded w-fit">TEAM</span>
                                  {truck.drivers.map(d => (
                                    <span key={d.id} className="text-slate-900 text-sm">{d.first_name} {d.last_name}</span>
                                  ))}
                                </div>
                              ) : truck.driver ? (
                                <span className="text-slate-900 text-sm">{truck.driver.first_name} {truck.driver.last_name}</span>
                              ) : (
                                <span className="text-slate-400 text-xs italic">No driver</span>
                              )}
                            </td>
                            <td className="px-3 py-1">
                              {truck.driver?.driver_type ? (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  truck.driver.driver_type === 'owner_operator' ? 'bg-purple-100 text-purple-700' :
                                  truck.driver.driver_type === 'company_driver' ? 'bg-blue-100 text-blue-700' :
                                  truck.driver.driver_type === 'rent' ? 'bg-orange-100 text-orange-700' :
                                  truck.driver.driver_type === 'buy' ? 'bg-green-100 text-green-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {truck.driver.driver_type === 'owner_operator' ? 'OWNER' :
                                   truck.driver.driver_type === 'company_driver' ? 'COMPANY' :
                                   truck.driver.driver_type === 'rent' ? 'RENT' :
                                   truck.driver.driver_type === 'buy' ? 'BUYING' :
                                   'OTHER'}
                                </span>
                              ) : (
                                <span className="text-gray-400 text-xs">-</span>
                              )}
                            </td>
                            <td 
                              className="px-3 py-1 cursor-pointer hover:bg-blue-100 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleSamsara(truck);
                              }}
                              title="Click to cycle: Yes → No → Prijavljena"
                            >
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                truck.samsara_enabled === 'Yes' ? 'bg-green-100 text-green-700' :
                                truck.samsara_enabled === 'No' ? 'bg-gray-100 text-gray-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {truck.samsara_enabled || 'No'}
                              </span>
                            </td>
                            <td className="px-3 py-1">
                              <span className="text-slate-600 text-xs font-mono">{truck.vin || '-'}</span>
                            </td>
                            <td className="px-3 py-1">
                              <span className="text-slate-700 text-xs">{truck.prepass || '-'}</span>
                            </td>
                            <td className="px-3 py-1">
                              <span className="text-slate-700 text-xs">{truck.license_plate || '-'}</span>
                            </td>
                            <td className="px-3 py-1">
                              <span className="text-slate-700 text-xs">{truck.make} {truck.model}</span>
                            </td>
                            <td className="px-3 py-1">
                              <span className="text-slate-700 text-xs">{truck.year || '-'}</span>
                            </td>
                            <td className="px-3 py-1">
                              <span className="text-slate-700 text-xs">
                                {formatDateNoTZ(truck.registration_expiration)}
                              </span>
                            </td>
                            <td className="px-3 py-1">
                              <span className="text-slate-700 text-xs">
                                {formatDateNoTZ(truck.dot_expiration)}
                              </span>
                            </td>
                            <td className="px-3 py-1">
                              {(() => {
                                const currentYear = new Date().getFullYear();
                                const iftaField = `ifta_${currentYear}` as keyof typeof truck;
                                const iftaValue = truck[iftaField];
                                const hasIfta = iftaValue && typeof iftaValue === 'string' && iftaValue.trim() !== '';
                                return (
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    hasIfta ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                  }`}>
                                    {hasIfta ? 'Yes' : 'No'}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-0 py-0">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedTruckForStatus(truck);
                                  setShowStatusModal(true);
                                }}
                                className={`w-full h-full flex items-center gap-1 px-3 py-2 text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${statusConfig[truck.truck_status as keyof typeof statusConfig]?.color || 'bg-green-100 text-green-700'}`}
                                title={statusConfig[truck.truck_status as keyof typeof statusConfig]?.description || 'Active'}
                              >
                                <Circle className="w-1.5 h-1.5 fill-current flex-shrink-0" />
                                <span className="truncate">{statusConfig[truck.truck_status as keyof typeof statusConfig]?.label || 'Active'}</span>
                              </button>
                            </td>
                            <td className="px-3 py-1" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center gap-1">
                                {['Safety', 'Superadmin', 'Admin'].includes(profile?.role || '') && (
                                  <>
                                    <button
                                      onClick={() => navigate(`/fleet/edit/${truck.id}`)}
                                      className="p-1 hover:bg-slate-200 rounded transition-colors"
                                    >
                                      <Edit className="w-3 h-3 text-slate-600" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setTruckToAction(truck);
                                        setShowDeleteConfirm(true);
                                      }}
                                      className="p-1 hover:bg-red-100 rounded transition-colors"
                                    >
                                      <Trash2 className="w-3 h-3 text-red-600" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selectedTruckIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-800 text-white p-4 rounded-lg shadow-lg flex items-center space-x-4 z-50">
          <span>{selectedTruckIds.size} trucks selected</span>
          {selectedTruckIds.size === 1 && (
            <button
              onClick={() => {
                const truckId = Array.from(selectedTruckIds)[0];
                navigate(`/fleet/edit/${truckId}`);
              }}
              className="px-3 py-1 bg-green-600 rounded hover:bg-green-700 transition-colors text-sm font-medium"
            >
              Edit
            </button>
          )}
          <button
            onClick={() => setShowMoveToModal(true)}
            className="px-3 py-1 bg-blue-600 rounded hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            Move to
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-3 py-1 bg-red-600 rounded hover:bg-red-700 transition-colors text-sm font-medium"
          >
            Delete
          </button>
          <button
            onClick={() => setSelectedTruckIds(new Set())}
            className="p-1 hover:bg-slate-700 rounded-full"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {showStatusModal && selectedTruckForStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-sm w-full">
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Change Status</h2>
                <button onClick={() => setShowStatusModal(false)} className="p-1 hover:bg-slate-100 rounded transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-slate-600 mt-1">{selectedTruckForStatus.unit_number}</p>
            </div>
            <div className="p-2 max-h-96 overflow-y-auto">
              <div className="space-y-1">
                {Object.entries(statusConfig).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => handleStatusChange(key)}
                    className="w-full px-3 py-2.5 text-left rounded transition-all flex items-center gap-2 hover:bg-slate-50"
                  >
                    <Circle className={`w-2 h-2 fill-current ${config.color.split(' ')[1]}`} />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{config.label}</div>
                      <div className="text-xs text-slate-500">{config.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showMoveToModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-sm w-full">
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Move to Status</h2>
                <button onClick={() => setShowMoveToModal(false)} className="p-1 hover:bg-slate-100 rounded transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-slate-600 mt-1">{selectedTruckIds.size} {selectedTruckIds.size === 1 ? 'truck' : 'trucks'} selected</p>
            </div>
            <div className="p-2 max-h-96 overflow-y-auto">
              <div className="space-y-1">
                {Object.entries(statusConfig).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => handleBatchStatusChange(key)}
                    className="w-full px-3 py-2.5 text-left rounded transition-all flex items-center gap-2 hover:bg-slate-50"
                  >
                    <Circle className={`w-2 h-2 fill-current ${config.color.split(' ')[1]}`} />
                    <span className="font-medium text-sm">{config.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showDriverStatusModal && selectedTruckForDriverStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-sm w-full">
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Change Driver Status</h2>
                <button onClick={() => setShowDriverStatusModal(false)} className="p-1 hover:bg-slate-100 rounded transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-slate-600 mt-1">{selectedTruckForDriverStatus.unit_number}</p>
            </div>
            <div className="p-2">
              <div className="space-y-1">
                <button
                  onClick={() => handleDriverStatusChange('OWNER')}
                  className="w-full px-3 py-2.5 text-left rounded transition-all flex items-center gap-2 hover:bg-purple-50"
                >
                  <Circle className="w-2 h-2 fill-current text-purple-700" />
                  <span className="font-medium text-sm">Owner</span>
                </button>
                <button
                  onClick={() => handleDriverStatusChange('COMPANY')}
                  className="w-full px-3 py-2.5 text-left rounded transition-all flex items-center gap-2 hover:bg-blue-50"
                >
                  <Circle className="w-2 h-2 fill-current text-blue-700" />
                  <span className="font-medium text-sm">Company</span>
                </button>
                <button
                  onClick={() => handleDriverStatusChange('RENT')}
                  className="w-full px-3 py-2.5 text-left rounded transition-all flex items-center gap-2 hover:bg-orange-50"
                >
                  <Circle className="w-2 h-2 fill-current text-orange-700" />
                  <span className="font-medium text-sm">Rent</span>
                </button>
                <button
                  onClick={() => handleDriverStatusChange('BUYING')}
                  className="w-full px-3 py-2.5 text-left rounded transition-all flex items-center gap-2 hover:bg-green-50"
                >
                  <Circle className="w-2 h-2 fill-current text-green-700" />
                  <span className="font-medium text-sm">Buy</span>
                </button>
                <button
                  onClick={() => handleDriverStatusChange('UNASSIGNED')}
                  className="w-full px-3 py-2.5 text-left rounded transition-all flex items-center gap-2 hover:bg-slate-50"
                >
                  <Circle className="w-2 h-2 fill-current text-slate-700" />
                  <span className="font-medium text-sm">Unassigned</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDispatcherModal && selectedTruckForDispatcher && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-purple-600 to-purple-700 text-white">
              <div>
                <h2 className="text-2xl font-bold">Assign Dispatcher</h2>
                <p className="text-sm text-purple-100">Select a dispatcher for {selectedTruckForDispatcher.unit_number}</p>
              </div>
              <button
                onClick={() => {
                  setShowDispatcherModal(false);
                  setSelectedTruckForDispatcher(null);
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search dispatchers by name..."
                    value={dispatcherSearchTerm}
                    onChange={(e) => setDispatcherSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    autoFocus
                  />
                </div>
              </div>
              <div className="space-y-2">
                {dispatchers.filter(d => 
                  d.full_name.toLowerCase().includes(dispatcherSearchTerm.toLowerCase()) ||
                  d.email.toLowerCase().includes(dispatcherSearchTerm.toLowerCase())
                ).length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <p className="mb-2">{dispatcherSearchTerm ? 'No dispatchers found' : 'No dispatchers available'}</p>
                    <p className="text-sm">{dispatcherSearchTerm ? 'Try a different search term' : 'No users with dispatcher role found'}</p>
                  </div>
                ) : (
                  dispatchers.filter(d => 
                    d.full_name.toLowerCase().includes(dispatcherSearchTerm.toLowerCase()) ||
                    d.email.toLowerCase().includes(dispatcherSearchTerm.toLowerCase())
                  ).map((dispatcher) => (
                    <button
                      key={dispatcher.id}
                      onClick={() => handleAssignDispatcher(dispatcher.id)}
                      disabled={assigningDispatcher}
                      className="w-full p-4 text-left border border-slate-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                          <span className="text-lg font-semibold text-purple-700">
                            {dispatcher.full_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900">
                            {dispatcher.full_name}
                          </p>
                          <p className="text-sm text-slate-600">{dispatcher.email}</p>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => {
                  setShowDispatcherModal(false);
                  setSelectedTruckForDispatcher(null);
                  setDispatcherSearchTerm('');
                }}
                className="w-full px-6 py-2.5 border border-slate-300 rounded-lg hover:bg-white transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showNoteModal && selectedTruckForNote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <div>
                <h2 className="text-2xl font-bold">Edit Note</h2>
                <p className="text-sm text-blue-100">Add or edit note for truck {selectedTruckForNote.unit_number}</p>
              </div>
              <button
                onClick={() => {
                  setShowNoteModal(false);
                  setSelectedTruckForNote(null);
                  setNoteInput('');
                  setNoteAttachment(null);
                  setNoteAttachmentUrl('');
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Note
                </label>
                <textarea
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  placeholder="Enter note for this truck..."
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Attachment (Image/Screenshot)
                </label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setNoteAttachment(file);
                    }
                  }}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                {noteAttachment && (
                  <div className="mt-2 text-sm text-slate-600">
                    Selected: {noteAttachment.name}
                  </div>
                )}
                {!noteAttachment && noteAttachmentUrl && (
                  <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-semibold text-slate-700">Current Attachment:</div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm('Are you sure you want to remove this attachment?')) {
                            setNoteAttachmentUrl('');
                          }
                        }}
                        className="text-xs px-2 py-1 text-red-600 hover:bg-red-50 rounded transition-colors flex items-center gap-1"
                        title="Remove attachment"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Remove
                      </button>
                    </div>
                    {noteAttachmentUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                      <SignedLink
                        stored={noteAttachmentUrl}
                        className="block"
                      >
                        <SignedImage
                          stored={noteAttachmentUrl}
                          alt="Note attachment"
                          className="w-full h-auto max-h-48 object-contain rounded border border-slate-300 hover:opacity-90 transition-opacity cursor-pointer"
                        />
                      </SignedLink>
                    ) : (
                      <SignedLink
                        stored={noteAttachmentUrl}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors text-sm"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        View PDF Attachment
                      </SignedLink>
                    )}
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-1">Accepts images and PDF files</p>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 bg-slate-50 flex gap-3">
              <button
                onClick={() => {
                  setShowNoteModal(false);
                  setSelectedTruckForNote(null);
                  setNoteInput('');
                  setNoteAttachment(null);
                  setNoteAttachmentUrl('');
                }}
                className="flex-1 px-6 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-white transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNote}
                disabled={savingNote}
                className="flex-1 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingNote ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showUnassignDispatcherConfirm}
        onCancel={() => {
          setShowUnassignDispatcherConfirm(false);
          setSelectedTruckForDispatcher(null);
        }}
        onConfirm={handleUnassignDispatcher}
        title="Unassign Dispatcher"
        message={`Are you sure you want to unassign the dispatcher from truck ${selectedTruckForDispatcher?.unit_number}?`}
        confirmText="Unassign"
        type="danger"
      />

      {showTrailerModal && selectedTruck && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-green-600 to-green-700 text-white">
              <div>
                <h2 className="text-2xl font-bold">Assign Trailer</h2>
                <p className="text-sm text-green-100">Update trailer for {selectedTruck.unit_number}</p>
              </div>
              <button
                onClick={() => {
                  setShowTrailerModal(false);
                  setSelectedTruck(null);
                  setTrailerInput('');
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6" onClick={() => setShowTrailerDropdown(false)}>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Trailer Unit Number
              </label>
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  value={trailerSearch || trailerInput}
                  onChange={(e) => {
                    setTrailerSearch(e.target.value);
                    setShowTrailerDropdown(true);
                    if (!e.target.value) {
                      setTrailerInput('');
                    }
                  }}
                  onClick={() => setShowTrailerDropdown(true)}
                  placeholder="Search trailers by unit number..."
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                {showTrailerDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {trailers
                      .filter(trailer => 
                        trailer.unit_number.toLowerCase().includes(trailerSearch.toLowerCase()) ||
                        `${trailer.make} ${trailer.model}`.toLowerCase().includes(trailerSearch.toLowerCase())
                      )
                      .map(trailer => (
                        <button
                          key={trailer.id}
                          type="button"
                          onClick={() => {
                            setTrailerInput(trailer.unit_number);
                            setTrailerSearch(trailer.unit_number);
                            setShowTrailerDropdown(false);
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-green-50 transition-colors border-b border-slate-100 last:border-0"
                        >
                          <div className="font-semibold text-slate-900">{trailer.unit_number}</div>
                          <div className="text-sm text-slate-600">{trailer.year} {trailer.make} {trailer.model}</div>
                        </button>
                      ))}
                    {trailers.filter(trailer => 
                      trailer.unit_number.toLowerCase().includes(trailerSearch.toLowerCase()) ||
                      `${trailer.make} ${trailer.model}`.toLowerCase().includes(trailerSearch.toLowerCase())
                    ).length === 0 && (
                      <div className="px-4 py-3 text-sm text-slate-500 text-center">
                        No trailers found
                      </div>
                    )}
                  </div>
                )}
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Leave empty to remove trailer assignment
              </p>
            </div>

            <div className="p-6 border-t border-slate-200 bg-slate-50 flex gap-3">
              <button
                onClick={() => {
                  setShowTrailerModal(false);
                  setSelectedTruck(null);
                  setTrailerInput('');
                  setTrailerSearch('');
                  setShowTrailerDropdown(false);
                }}
                className="flex-1 px-6 py-2.5 border border-slate-300 rounded-lg hover:bg-white transition-colors font-medium"
              >
                Cancel
              </button>
              {selectedTruck.trailers && (
                <button
                  onClick={() => {
                    setTrailerInput('');
                    setTrailerSearch('');
                    setShowTrailerDropdown(false);
                    handleAssignTrailer('');
                  }}
                  className="flex-1 px-6 py-2.5 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium"
                >
                  Unassign Trailer
                </button>
              )}
              <button
                onClick={() => handleAssignTrailer()}
                className="flex-1 px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
              >
                {trailerInput ? 'Update Trailer' : 'Assign Trailer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDriverModal && selectedTruck && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <div>
                <h2 className="text-2xl font-bold">{teamMode ? 'Assign Team' : 'Assign Driver'}</h2>
                <p className="text-sm text-blue-100">
                  {teamMode
                    ? `Select 2 drivers for ${selectedTruck.unit_number} (${selectedTeamDriverIds.length}/2)`
                    : `Select a driver for ${selectedTruck.unit_number}`}
                </p>
              </div>
              <button
                onClick={closeDriverModal}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* Team driver toggle */}
              <label className="mb-4 flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={teamMode}
                  onChange={(e) => { setTeamMode(e.target.checked); setSelectedTeamDriverIds([]); }}
                  className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-slate-700">Team driver (assign 2 drivers to this truck)</span>
              </label>

              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search drivers by name..."
                    value={driverSearchTerm}
                    onChange={(e) => setDriverSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                </div>
              </div>
              <div className="space-y-2">
                {drivers.filter(d => (!assignedDriverIds.has(d.id) || selectedTeamDriverIds.includes(d.id)) &&
                  `${d.first_name} ${d.last_name}`.toLowerCase().includes(driverSearchTerm.toLowerCase())
                ).length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <p className="mb-2">{driverSearchTerm ? 'No drivers found' : 'No available drivers'}</p>
                    <p className="text-sm">{driverSearchTerm ? 'Try a different search term' : 'All active drivers are currently assigned to trucks'}</p>
                  </div>
                ) : (
                  drivers.filter(d => (!assignedDriverIds.has(d.id) || selectedTeamDriverIds.includes(d.id)) &&
                    `${d.first_name} ${d.last_name}`.toLowerCase().includes(driverSearchTerm.toLowerCase())
                  ).map((driver) => {
                    const isSelected = selectedTeamDriverIds.includes(driver.id);
                    const capReached = teamMode && !isSelected && selectedTeamDriverIds.length >= 2;
                    return (
                    <button
                      key={driver.id}
                      onClick={() => teamMode ? toggleTeamDriver(driver.id) : handleAssignDriver(driver.id)}
                      disabled={assigningDriver || capReached}
                      className={`w-full p-4 text-left border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        isSelected ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500' : 'border-slate-200 hover:border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-lg font-semibold text-blue-700">
                            {driver.first_name[0]}{driver.last_name[0]}
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900">
                            {driver.first_name} {driver.last_name}
                          </p>
                          <p className="text-sm text-slate-600">{driver.phone}</p>
                        </div>
                        {teamMode && isSelected && (
                          <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-1 rounded-full">SELECTED</span>
                        )}
                      </div>
                    </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 bg-slate-50 flex gap-3">
              {teamMode && (
                <button
                  onClick={handleAssignTeam}
                  disabled={assigningDriver || selectedTeamDriverIds.length !== 2}
                  className="flex-1 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {assigningDriver ? 'Assigning…' : 'Assign Team'}
                </button>
              )}
              <button
                onClick={closeDriverModal}
                className={`${teamMode ? 'flex-1' : 'w-full'} px-6 py-2.5 border border-slate-300 rounded-lg hover:bg-white transition-colors font-medium`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showUnassignConfirm}
        title={truckToAction?.team_id ? 'Unassign Team' : 'Unassign Driver'}
        message={truckToAction && truckToAction.drivers && truckToAction.drivers.length > 1
          ? `Are you sure you want to unassign the team (${truckToAction.drivers.map(d => `${d.first_name} ${d.last_name}`).join(' & ')}) from ${truckToAction.unit_number}? The team will be disbanded.`
          : truckToAction && truckToAction.driver
          ? `Are you sure you want to unassign ${truckToAction.driver.first_name} ${truckToAction.driver.last_name} from ${truckToAction.unit_number}?`
          : 'Are you sure you want to unassign this driver?'}
        confirmText="Unassign"
        cancelText="Cancel"
        onConfirm={handleUnassignDriver}
        onCancel={() => {
          setShowUnassignConfirm(false);
          setTruckToAction(null);
        }}
        type="warning"
      />

      {/* Dispatcher unassign modal temporarily disabled
      <ConfirmModal
        isOpen={showUnassignDispatcherConfirm}
        title="Unassign Dispatcher"
        message={selectedTruckForDispatcher && selectedTruckForDispatcher.dispatcher
          ? `Are you sure you want to unassign ${selectedTruckForDispatcher.dispatcher.first_name} ${selectedTruckForDispatcher.dispatcher.last_name} from ${selectedTruckForDispatcher.unit_number}?`
          : 'Are you sure you want to unassign this dispatcher?'}
        confirmText="Unassign"
        cancelText="Cancel"
        onConfirm={handleUnassignDispatcher}
        onCancel={() => {
          setShowUnassignDispatcherConfirm(false);
          setSelectedTruckForDispatcher(null);
        }}
        type="warning"
      />
      */}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title={selectedTruckIds.size > 0 ? "Delete Trucks" : "Delete Truck"}
        message={selectedTruckIds.size > 0 
          ? `Are you sure you want to delete ${selectedTruckIds.size} truck(s)? This action cannot be undone.`
          : truckToAction
          ? `Are you sure you want to delete ${truckToAction.unit_number}? This action cannot be undone.`
          : 'Are you sure you want to delete this truck?'}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={selectedTruckIds.size > 0 ? handleBatchDelete : handleDeleteTruck}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setTruckToAction(null);
        }}
        type="danger"
      />
    </div>
  );
}
